<template>
    <el-form :model="form" ref="formRef" class="sif-form">
        <div class="sif-grid">
            <div class="sif-field sif-field--span-2">
                <label class="sif-label sif-label--required">{{ $t('pages.stock.save.cols.name') }}</label>
                <el-form-item prop="name" :rules="$validation.getMessage(['required'])">
                    <el-input v-model="form.name" type="text"/>
                </el-form-item>
            </div>

            <div class="sif-field">
                <label class="sif-label">{{ $t('pages.stock.save.cols.code') }}</label>
                <el-form-item prop="code">
                    <el-input v-model="form.code" type="text" :placeholder="$t('pages.stock.save.codePlaceholder')"/>
                </el-form-item>
            </div>

            <div class="sif-field">
                <label class="sif-label sif-label--required">{{ $t('pages.stock.save.cols.category') }}</label>
                <el-form-item prop="stock_category_id" :rules="$validation.getMessage(['required'])">
                    <el-tree-select v-model="form.stock_category_id" class="w-100"
                        :data="categoryTree"
                        :props="{ value: 'id', label: 'name', children: 'children' }"
                        node-key="id"
                        check-strictly
                        :render-after-expand="false"
                        default-expand-all
                        filterable
                        :placeholder="$t('common.chooseSelect')"/>
                </el-form-item>
            </div>

            <div class="sif-field">
                <label class="sif-label sif-label--required">{{ $t('pages.stock.save.cols.unit_type') }}</label>
                <el-form-item>
                    <el-select v-model="form.unit_id" class="w-100"
                        :placeholder="$t('common.chooseSelect')" filterable>
                        <el-option v-for="item in unitTypes" :key="item.id" :value="item.id" :label="item.name"/>
                    </el-select>
                </el-form-item>
            </div>

            <div class="sif-field">
                <label class="sif-label">{{ $t('pages.stock.save.cols.min_quantity') }}</label>
                <el-form-item prop="min_quantity">
                    <el-input v-model="form.min_quantity"
                        oninput="value=value.replace(/[^0-9.]/g,'')"
                        :placeholder="$t('pages.stock.save.minQuantityPlaceholder')"/>
                </el-form-item>
            </div>

            <div class="sif-field">
                <label class="sif-label">{{ $t('pages.stock.save.cols.sale_price') }}</label>
                <el-form-item prop="sale_price">
                    <el-input v-model="form.sale_price"
                        oninput="value=value.replace(/[^0-9.]/g,'')" placeholder="0,00"/>
                </el-form-item>
            </div>

            <div class="sif-field sif-field--span-2">
                <label class="sif-label">{{ $t('common.status') }}</label>
                <el-form-item prop="active">
                    <el-radio-group v-model="form.active">
                        <el-radio-button :label="$t('common.active')" :value="true"/>
                        <el-radio-button :label="$t('common.passive')" :value="false"/>
                    </el-radio-group>
                </el-form-item>
            </div>
        </div>

        <div class="sif-foot">
            <button type="button" @click.prevent="onSubmit"
                :data-kt-indicator="loading ? 'on' : null"
                class="btn btn-primary"
                :disabled="loading">
                <span v-if="!loading" class="indicator-label">{{ submitLabel }}</span>
                <span v-if="loading" class="indicator-progress">
                    {{ $t("messages.wait") }}
                    <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
            </button>
        </div>
    </el-form>
</template>

<script>
function emptyForm() {
    return {
        stock_category_id: null,
        unit_id: null,
        code: null,
        name: null,
        sale_price: null,
        min_quantity: null,
        active: true,
    };
}

export default {
    name: "StockInfoForm",
    props: {
        stock: { type: Object, default: null },
        isCreate: { type: Boolean, default: false },
    },
    emits: ['saved'],
    data() {
        return {
            form: emptyForm(),
            loading: false,
            categoryTree: [],
        };
    },
    computed: {
        unitTypes() {
            return this.$root.unitTypes || [];
        },
        submitLabel() {
            return this.isCreate ? this.$t('pages.stock.newStock') : this.$t('btn.save');
        },
    },
    watch: {
        stock: {
            handler(val) {
                if (val && !this.isCreate) {
                    this.form = {
                        stock_category_id: val.stock_category_id,
                        unit_id: val.unit_id,
                        code: val.code,
                        name: val.name,
                        sale_price: val.sale_price,
                        min_quantity: val.min_quantity,
                        active: !!val.active,
                    };
                }
            },
            immediate: true,
            deep: true,
        },
    },
    mounted() {
        this.fetchCategoryTree();
    },
    methods: {
        async fetchCategoryTree() {
            try {
                const res = await this.axios.get(this.endpoints['company_stock_category_tree']);
                this.categoryTree = res.data?.data || [];
            } catch (e) { /* empty */ }
        },
        onSubmit() {
            this.$refs.formRef.validate(async (valid) => {
                if (!valid) return false;
                this.loading = true;
                try {
                    const action = this.isCreate ? 'stock/add' : 'stock/update';
                    const payload = this.isCreate
                        ? { formData: this.form }
                        : { id: this.stock?.id, formData: this.form };
                    const res = await this.$store.dispatch(action, payload);
                    if (res?.status) {
                        this.$message.success(this.$t('messages.saveOk'));
                        this.$emit('saved', { id: res.data?.id, isCreate: this.isCreate });
                    }
                } finally {
                    this.loading = false;
                }
            });
        },
    },
};
</script>

<style scoped>
.sif-form {
    padding: 24px 26px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.04);
}

html.dark .sif-form {
    background: #15171C;
    border-color: rgba(255, 255, 255, 0.07);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25);
}

html.midnight .sif-form {
    background: #1A1714;
    border-color: rgba(255, 255, 255, 0.07);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25);
}

html.cream .sif-form {
    background: #FEFCF8;
    border-color: rgba(61, 56, 50, 0.1);
}

.sif-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 20px;
}

.sif-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.sif-field--span-2 {
    grid-column: 1 / -1;
}

.sif-label {
    font-size: 12px;
    font-weight: 600;
    color: #1c1c1e;
}

.sif-label--required::after {
    content: ' *';
    color: #ef4444;
}

.sif-foot {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    justify-content: flex-end;
}

@media (max-width: 720px) {
    .sif-grid { grid-template-columns: 1fr; }
}

html.dark .sif-label,
html.midnight .sif-label { color: #EAE6E1; }
html.cream .sif-label { color: #3D3832; }

html.dark .sif-foot,
html.midnight .sif-foot { border-top-color: rgba(255, 255, 255, 0.07); }
html.cream .sif-foot { border-top-color: rgba(61, 56, 50, 0.08); }
</style>
